import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/lib/api";
import { SEALING_TIPS, TIP_UI, GRAPHIC_FILES } from "@/lib/sealingTips";
import { GraphicDownloads } from "@/components/GraphicDownloads";

const ALT = {
  en: "Cutaway of a house showing seven ways radon can enter: cracks in floors and foundation walls, pipe and utility penetrations, floor drains and sump pits, construction joints, crawl spaces and dirt floors, window wells and other openings, and well water.",
  es: "Corte de una casa que muestra siete formas en que el radón puede entrar: grietas en pisos y paredes de cimientos, penetraciones de tuberías y servicios, desagües de piso y fosos de sumidero, juntas de construcción, espacios de acceso y pisos de tierra, pozos de ventanas y otras aberturas, y agua de pozo.",
};

const SaveTip = ({ tipId, ui, authed, saved, onAdd }) => {
  if (!authed) {
    return (
      <Link to="/login" className="text-xs font-semibold text-[#2F524A] hover:underline mt-3 inline-block"
        data-testid={`entry-point-login-${tipId}`}>
        {ui.login} →
      </Link>
    );
  }
  return saved ? (
    <p className="text-xs font-semibold text-[#2F524A] mt-3 inline-flex items-center gap-1"
      data-testid={`entry-point-added-${tipId}`}>
      <Check size={14} /> {ui.added} ·{" "}
      <Link to="/dashboard" className="underline">{ui.dashboard}</Link>
    </p>
  ) : (
    <button type="button" onClick={() => onAdd(tipId)} data-testid={`entry-point-add-${tipId}`}
      className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1 mt-3">
      <Plus size={12} /> {ui.add}
    </button>
  );
};

const Hotspot = ({ p, lang, authed, saved, onAdd }) => {
  const t = p[lang];
  const ui = TIP_UI[lang];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${p.n}: ${t.title}`}
          data-testid={`entry-point-hotspot-${p.n}`}
          className="absolute -translate-x-1/2 -translate-y-1/2 w-[7%] min-w-[32px] aspect-square rounded-full ring-2 ring-white/0 hover:ring-white focus-visible:ring-white focus-visible:outline-none transition-shadow"
          style={{ left: `${p.x}%`, top: `${p.y}%` }}
        >
          <span className="absolute inset-0 rounded-full animate-ping opacity-40" style={{ background: p.color }} />
        </button>
      </PopoverTrigger>
      <PopoverContent collisionPadding={12} className="w-72 bg-white" data-testid={`entry-point-tip-${p.n}`} lang={lang}>
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0"
            style={{ background: p.color }}>{p.n}</span>
          <p className="font-serif text-base text-[#1C201F] leading-tight">{t.title}</p>
        </div>
        <p className="eyebrow mt-3">{ui.howTo}</p>
        <p className="text-sm text-[#1C201F] mt-1 leading-relaxed">{t.tip}</p>
        <SaveTip tipId={p.id} ui={ui} authed={authed} saved={saved} onAdd={onAdd} />
        <p className="text-xs text-[#5C6661] mt-3 leading-relaxed border-t border-[#E8ECEA] pt-2">{ui.note}</p>
      </PopoverContent>
    </Popover>
  );
};

export const RadonEntryGraphic = ({ lang: controlledLang }) => {
  const { user } = useAuth();
  const [ownLang, setLang] = useState("en");
  const lang = controlledLang || ownLang;
  const [saved, setSaved] = useState([]);
  const current = GRAPHIC_FILES.find((l) => l.id === lang);
  const ui = TIP_UI[lang];

  useEffect(() => {
    if (!user) return;
    api.get("/action-plan").then(({ data }) => setSaved(data.sealing_tips || [])).catch(() => {});
  }, [user]);

  const addTip = async (id) => {
    setSaved((s) => [...s, id]);
    try {
      const { data } = await api.post("/action-plan/sealing-tips", { tip_id: id });
      setSaved(data.sealing_tips || []);
    } catch {
      setSaved((s) => s.filter((x) => x !== id));
    }
  };

  return (
    <figure className="mt-2" data-testid="learn-entry-points-figure">
      {!controlledLang && <div className="flex justify-end mb-2">
        <div className="inline-flex rounded-full border border-[#E8ECEA] p-1" role="group" aria-label="Graphic language">
          {GRAPHIC_FILES.map((l) => (
            <button key={l.id} type="button" onClick={() => setLang(l.id)} data-testid={`entry-points-lang-${l.id}`}
              aria-pressed={lang === l.id}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                lang === l.id ? "bg-[#2F524A] text-white" : "text-[#1C201F] hover:bg-[#F4F7F6]"}`}>
              {l.label}
            </button>
          ))}
        </div>
      </div>}
      <div className="relative">
        <img src={current.full} alt={ALT[lang]} className="w-full h-auto rounded-xl border border-[#E8ECEA]"
          width="1536" height="1024" data-testid="learn-entry-points-image" />
        {SEALING_TIPS.map((p) => (
          <Hotspot key={p.id} p={p} lang={lang} authed={!!user} saved={saved.includes(p.id)} onAdd={addTip} />
        ))}
      </div>
      <figcaption className="text-xs text-[#5C6661] mt-2 flex flex-wrap justify-between gap-2">
        <span data-testid="entry-points-caption">{ui.caption}</span>
        <a href={current.full} target="_blank" rel="noopener noreferrer" className="text-[#2F524A] font-semibold hover:underline"
          data-testid="learn-entry-points-image-link">
          {ui.fullSize}
        </a>
      </figcaption>
      <div className="mt-4">
        <p className="eyebrow mb-2">{ui.share}</p>
        <GraphicDownloads compact lang={lang} />
      </div>
    </figure>
  );
};
